import * as React from 'react';
import {
  useLayoutEffect,
  DetailedHTMLProps,
  HTMLAttributes,
  useState,
  Fragment,
  useEffect,
  useRef,
  KeyboardEvent,
} from 'react';
import { ChevronRight } from '@tcl-argon-icons/icons';
import { memoize } from 'lodash';
import { mergeClasses } from '@tcl-argon-utils/utils';

import { useMenuStyles, MenuClasses } from './styles';
import { MenuDirection, getMenuDirection } from './menuDirection';
import {
  NAVIGATION_KEYS,
  ACTION_KEYS,
  ActionKey,
  NavigationKey,
  getNewFocusIndexStack,
  handleActionKeyPress,
} from './keyboardNavigation';

export interface MenuItem {
  disabled?: boolean;
  Icon?: React.ComponentType<{ className?: string }>;
  subMenu?: MenuItems;
  text: string;
  id: string;
}
export type MenuItemGroup = MenuItem[];
export type MenuItems = MenuItemGroup[];

export interface InternalMenuItem extends Omit<MenuItem, 'subMenu'> {
  index: number;
  subMenu?: InternalMenuItems;
}
export type InternalMenuItemGroup = InternalMenuItem[];
export type InternalMenuItems = InternalMenuItemGroup[];

interface MenuItemRowProps extends InternalMenuItem {
  classes: MenuClasses;
  setOpenSubMenuIndex: React.Dispatch<React.SetStateAction<number | null>>;
  openSubMenuIndex: number | null;
  focusIndexStack: number[];
  setFocusIndexStack: React.Dispatch<React.SetStateAction<number[]>>;
  onChange: (value?: string) => void;
  onClose: () => void;
  className: string;
}
const MenuItemRow = ({
  Icon,
  onChange,
  subMenu,
  text,
  id,
  classes,
  disabled = false,
  index,
  setOpenSubMenuIndex,
  setFocusIndexStack,
  focusIndexStack,
  onClose,
  className,
}: MenuItemRowProps) => {
  const isFocused = !disabled && focusIndexStack.length === 1 && focusIndexStack[0] === index;
  const onRowClick: React.MouseEventHandler<HTMLDivElement> = () => {
    if (disabled || subMenu !== undefined) return;
    onChange(id);
    onClose();
  };
  return (
    <div
      className={`${classes.menuItemRow}  ${disabled ? classes.menuItemRowDisabled : classes.menuItemRowEnabled} ${
        isFocused ? classes.menuItemRowFocused : classes.menuItemRowNotFocused
      } ${className}`}
      onMouseDown={(evt) => evt.preventDefault()} // prevent taking focus
      onClick={onRowClick}
      onMouseEnter={
        !disabled
          ? () => {
              setOpenSubMenuIndex(index);
              setFocusIndexStack([]);
            }
          : () => {}
      }
    >
      <div className={classes.menuItemRowContent}>
        {Icon && <Icon className={classes.menuItemIcon} />}
        <span>{text}</span>
      </div>
      {subMenu !== undefined && <ChevronRight className={classes.menuItemIcon} style={{ margin: 0 }} />}
    </div>
  );
};

type MenuItemGroupProps = {
  menuItemGroup: InternalMenuItemGroup;
  classes: MenuClasses;
  setOpenSubMenuIndex: React.Dispatch<React.SetStateAction<number | null>>;
  openSubMenuIndex: number | null;
  initialDirection: MenuDirection;
  focusIndexStack: number[];
  setFocusIndexStack: React.Dispatch<React.SetStateAction<number[]>>;
  onClose: () => void;
  onChange: (key?: string) => void;
};
const MenuItemGroup = ({
  menuItemGroup,
  classes,
  openSubMenuIndex,
  setOpenSubMenuIndex,
  initialDirection,
  focusIndexStack,
  setFocusIndexStack,
  onClose,
  onChange,
}: MenuItemGroupProps) => (
  <div>
    {menuItemGroup.map((menuItem) => {
      const subMenuOpenViaFocus = focusIndexStack.length >= 2 && focusIndexStack[0] === menuItem.index;
      const subMenuOpenViaHover = openSubMenuIndex === menuItem.index;
      const subMenuOpen = menuItem.subMenu !== undefined && (subMenuOpenViaFocus || subMenuOpenViaHover);
      const menuItemRow = (
        <MenuItemRow
          key={menuItem.id}
          className={subMenuOpen ? classes.menuRootSubMenuOpen : ''}
          {...{
            classes,
            openSubMenuIndex,
            setOpenSubMenuIndex,
            focusIndexStack,
            setFocusIndexStack,
            onClose,
            onChange,
            ...menuItem,
          }}
        />
      );
      // first element represents the root parent node
      return menuItem.subMenu !== undefined && (subMenuOpenViaFocus || subMenuOpenViaHover) ? (
        <MenuR
          key={menuItem.id}
          {...{
            onClose,
            menuItems: menuItem.subMenu,
            isOpen: true,
            isSubMenu: true,
            focusIndexStack: focusIndexStack.slice(1),
            setFocusIndexStack,
            initialDirection,
            onChange,
          }}
        >
          {menuItemRow}
        </MenuR>
      ) : (
        menuItemRow
      );
    })}
  </div>
);

type MenuItemsListProps = {
  menuItems: InternalMenuItems;
  classes: MenuClasses;
  setOpenSubMenuIndex: React.Dispatch<React.SetStateAction<number | null>>;
  openSubMenuIndex: number | null;
  initialDirection: MenuDirection;
  focusIndexStack: number[];
  setFocusIndexStack: React.Dispatch<React.SetStateAction<number[]>>;
  onClose: () => void;
  onChange: (key?: string) => void;
};
const MenuItemsList = ({
  menuItems,
  classes,
  setOpenSubMenuIndex,
  openSubMenuIndex,
  initialDirection,
  focusIndexStack,
  setFocusIndexStack,
  onClose,
  onChange,
}: MenuItemsListProps) => {
  useEffect(() => () => setOpenSubMenuIndex(null), [setOpenSubMenuIndex]);
  return (
    <div className={classes.menuItemsList}>
      {menuItems.map((itemGroup) => (
        <MenuItemGroup
          key={itemGroup[0].id}
          {...{
            initialDirection,
            classes,
            setOpenSubMenuIndex,
            openSubMenuIndex,
            focusIndexStack,
            setFocusIndexStack,
            onClose,
            onChange,
            menuItemGroup: itemGroup,
          }}
        />
      ))}
    </div>
  );
};

interface MenuRProps extends Omit<MenuProps, 'menuItems' | 'value'> {
  isSubMenu: boolean;
  initialDirection?: MenuDirection;
  focusIndexStack: number[];
  menuItems: InternalMenuItems;
  setFocusIndexStack: React.Dispatch<React.SetStateAction<number[]>>;
}
const MenuR: React.FC<MenuRProps> = ({
  children: anchor,
  menuItems,
  onChange = () => {},
  isOpen,
  onClose = () => {},
  isSubMenu,
  direction,
  initialDirection,
  focusIndexStack,
  setFocusIndexStack,
  className,
  classes: classOverrides = {},
  ...props
}) => {
  const [openSubMenuIndex, setOpenSubMenuIndex] = useState<null | number>(null);
  const [menuDirection, setMenuDirection]: [MenuDirection, React.Dispatch<React.SetStateAction<MenuDirection>>] =
    useState(direction || 'side-right-down');
  const anchorRef: React.MutableRefObject<HTMLDivElement | null> = useRef(null);

  const updateMenuDirection = () => {
    if (direction === undefined && anchorRef.current !== null) {
      const { top, bottom, left, right } = anchorRef.current.getBoundingClientRect();
      const newDirection = getMenuDirection(menuItems, top, bottom, left, right, isSubMenu, initialDirection);
      if (newDirection !== menuDirection) {
        setMenuDirection(newDirection);
      }
    }
  };
  useLayoutEffect(() => {
    updateMenuDirection();
  }, [menuItems]);
  updateMenuDirection();

  // we should make mergeClasses a generic, but it's
  const classes = mergeClasses(
    useMenuStyles({ menuDirection, theme: {} }),
    classOverrides,
    className ? { root: className } : {},
  ) as MenuClasses;

  const keyListener = (event: KeyboardEvent<HTMLDivElement>) => {
    if (NAVIGATION_KEYS.includes(event.key as NavigationKey)) {
      event.preventDefault();
      setFocusIndexStack(getNewFocusIndexStack(menuItems, focusIndexStack, event.key as NavigationKey));
    }
    if (ACTION_KEYS.includes(event.key as ActionKey)) {
      event.preventDefault();
      handleActionKeyPress(menuItems, focusIndexStack, onChange, onClose, event.key as ActionKey);
    }
  };
  return (
    <div className={classes.root} data-analytics="menu" {...props}>
      <div ref={anchorRef} onKeyDown={isOpen ? keyListener : () => {}}>
        {anchor}
      </div>
      {isOpen && (
        <Fragment>
          {!isSubMenu && <div className={classes.backgroundLayer} onClick={onClose} />}
          <div className={`${classes.menuItemsContainer} ${classes.menuDirection}`}>
            {!isSubMenu && <div className={classes.caret} />}
            <MenuItemsList
              {...{
                onClose,
                onChange,
                menuItems,
                initialDirection: initialDirection || menuDirection,
                classes,
                setOpenSubMenuIndex,
                openSubMenuIndex,
                focusIndexStack,
                setFocusIndexStack,
              }}
            />
          </div>
        </Fragment>
      )}
    </div>
  );
};

// overall index is the index of an item in a menu regardless of its menuItemGroup
// this is used for things like focusing and navigation over members of multiple groups
// as if the items were all part of one larger group.
// internalId is used for unique keys in maps
const menuItemsToInternalMenuItems = memoize((menuItems: MenuItems): InternalMenuItems => {
  let overallIndex = 0;
  return menuItems.map((itemGroup) =>
    itemGroup.map((menuItem) => {
      const newItem = {
        ...menuItem,
        index: overallIndex,
        subMenu: menuItem.subMenu === undefined ? undefined : menuItemsToInternalMenuItems(menuItem.subMenu),
      };
      overallIndex = overallIndex + 1;
      return newItem;
    }),
  );
});

export interface MenuProps extends Omit<DetailedHTMLProps<HTMLAttributes<HTMLDivElement>, HTMLDivElement>, 'onChange'> {
  /** element relative to which the menu will render */
  children: JSX.Element;
  /** menus can be stateful and have selected items like a dropdown (think app switcher). This behavior will be ignored unless value is passed in */
  value?: string;
  /** triggered when a user clicks a menu item */
  onChange?: (key?: string) => void;
  /** triggered when a user clicks outside of the menu */
  onClose?: () => void;
  /** prop to override automatic menu placement with a forced relative placement 'above-right' | 'above-left' | 'below-right' | 'below-left' | 'side-right' | 'side-left' */
  direction?: MenuDirection;
  /** list of options that can be in a menu, organized into groups of MenuItem and recursive sub-MenuItems*/
  menuItems: MenuItems;
  /** determines if menu is open or not */
  isOpen: boolean;
  /** Style classNames to be applied in addition to the base classNames */
  classes?: Record<string, string>;
}
export const Menu: React.FC<MenuProps> = ({ menuItems, isOpen, onChange = () => {}, onClose = () => {}, ...props }) => {
  const [previousIsOpen, setPreviousIsOpen] = useState(isOpen);

  // focus index stack is a stack representing the "path" from the root menu to the focused menu item.
  // the length of the stack corresponds to the number of submenus that are open (1 if just the root menu is open, 0 if nothing is focused).
  // the top of the stack represents the focused element. the menu will know that its element is focused because by the time it
  // renders the stack will have been recursively shortened until there's just one item in it, so from the perspective
  // of the leaf menu, it is the only open menu and its item is focused at that final index.
  // finally, a menuitem can know to render its submenu if the stack is 2 items long or greater (indicating that this is not the leaf menu), and
  // if the first element of the array matches that menu item's index. This is beacuse from that particular submenu's perspective, a submenu
  // must be open as there's multiple levels to the stack and since it is the root-most menu, the first element in the stack belongs to it
  const [focusIndexStack, setFocusIndexStack]: [number[], React.Dispatch<React.SetStateAction<number[]>>] = useState(
    [] as number[],
  );

  // reset focus stack if menu open state is toggled
  if (previousIsOpen !== isOpen) {
    setFocusIndexStack([]);
    setPreviousIsOpen(isOpen);
  }

  const menuItems_ = menuItemsToInternalMenuItems(menuItems);

  return (
    <MenuR
      {...{
        isSubMenu: false,
        menuItems: menuItems_,
        onChange,
        onClose,
        isOpen,
        setFocusIndexStack,
        focusIndexStack,
        ...props,
      }}
    />
  );
};
